/**
 * Reine Regeln der Bestellliste v2 (`OrderList.tsx`): was eine Zeile anzeigt, wonach sie sortiert, sucht,
 * filtert und exportiert. Keine React-Abhängigkeit — Plan `.claude/plans/2026-10-20-bestellliste-v2.md`.
 *
 * Wörter wie die Kacheln derselben Seite (Testphase / Quartalsabo / Jahresabo / Einmalfaktura / Unbezahlt /
 * Verweigert) und die Begriffe der Registry (`helpers/kpiDefinitions.ts`). Geld brutto wie die Kachel „Umsatz“.
 *
 * `sektion` (= Netto-Modus der Liste auf /auswertungen/bestellungen, P5-Review 27.10.2026): Kanal und Blog heißen wie
 * in den Übersichten derselben Seite (`channel_label`, `blog_sektion` vom Server), Kundentypen wie in der Sektion
 * (`TERMS.*.sektionLabel` bzw. `label`), und Suche/Sortierung/Export lesen dieselben Namen. Ohne den Schalter bleibt
 * die alte Liste (/plx-orders/alt) unverändert.
 */
import { formatEur, TERMS } from '../../helpers/kpiDefinitions';
import { PlxOrderListRow } from '../../helpers/plxOrdersData';

/** `YYYY-MM-DD` oder `YYYY-MM-DD HH:MM:SS` (Berliner Wallclock) → `19.10.26`. Kein `Date`: keine Zonenrechnung. */
export const datumKurz = (v: string | null | undefined): string => {
  if (!v) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  return m ? `${m[3]}.${m[2]}.${m[1].slice(2)}` : v;
};

/** Wallclock → `19.10.26 21:46`. */
export const datumZeit = (v: string | null | undefined): string => {
  if (!v) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(v);
  return m ? `${m[3]}.${m[2]}.${m[1].slice(2)} ${m[4]}:${m[5]}` : datumKurz(v);
};

// ── Stand (Abo-Typ + Vertrag) ───────────────────────────────────────────────────────────────────────────

export type StandTon = 'test' | 'bezahlt' | 'offen' | 'abgelehnt' | 'vorlaeufig' | 'neutral';

export interface Stand {
  label: string;
  ton: StandTon;
  /** Zweite Zeile: Testphasen-Ende, Grund für „Unbezahlt“, Vertragsstand. */
  detail: string;
  tip: string;
  /** Reihenfolge beim Sortieren nach Stand. */
  rang: number;
}

/** „läuft“ (L) ist der Normalfall und steht nicht da — nur, was davon abweicht. */
const VERTRAG: Record<string, string> = { W: 'widerrufen', B: 'beendet' };

/** Vertragsstand laut PL-X in Kurzform, mit Kündigung und Aboende. */
export const vertragText = (r: PlxOrderListRow): string => {
  const teile: string[] = [];
  if (r.kuendigungsdatum) teile.push(`gekündigt ${datumKurz(r.kuendigungsdatum)}`);
  else if (r.vertrag_sts && VERTRAG[r.vertrag_sts]) teile.push(VERTRAG[r.vertrag_sts]);
  if (r.aboende_datum && (r.kuendigungsdatum || r.vertrag_sts === 'B'))
    teile.push(`Ende ${datumKurz(r.aboende_datum)}`);
  return teile.join(' · ');
};

/** `2026-11-18` → `18.11.` — das Testphasen-Ende liegt immer in den nächsten Wochen. */
const tagMonat = (v: string | null): string => {
  const m = v ? /^\d{4}-(\d{2})-(\d{2})/.exec(v) : null;
  return m ? `${m[2]}.${m[1]}.` : '';
};

const unbezahltGrund = (r: PlxOrderListRow): string => {
  if (r.vertrag_sts === 'W') return 'widerrufen';
  if (r.vertrag_sts === 'B') return 'in der Testphase beendet';
  if (r.is_upsell && (r.zahlung_brutto ?? 0) > 1) return 'Jahresrechnung offen';
  switch (r.invoice_stage) {
    case 'keine_rechnung':
      return 'noch keine Rechnung';
    case 'fakturiert_unbezahlt':
      return 'Rechnung nicht bezahlt';
    case 'nur_testphase':
      return 'nur Testphasen-Rechnung';
    default:
      return r.zahlung_brutto === null ? 'noch keine Rechnung' : 'keine Zahlung über 1 €';
  }
};

const mit = (...teile: string[]): string => teile.filter((t) => t).join(' · ');

export const stand = (r: PlxOrderListRow): Stand => {
  if (r.is_provisional) {
    return {
      label: TERMS.vorlaeufig.short,
      ton: 'vorlaeufig',
      detail: 'Zahlstand unbekannt',
      tip: TERMS.vorlaeufig.tip,
      rang: 6,
    };
  }
  if (r.vertrag_sts === 'V') {
    return {
      label: 'Verweigert',
      ton: 'abgelehnt',
      detail: 'keine Anfo',
      tip: 'PL-X hat die Bestellung nicht angenommen — meist hat die Adresse das Produkt schon. Zählt nicht als Anfo.',
      rang: 7,
    };
  }
  const v = vertragText(r);
  switch (r.subscription_type) {
    case 'Testphase':
      return {
        label: 'Testphase',
        ton: 'test',
        detail: mit(r.trial_end ? `bis ${tagMonat(r.trial_end)}` : '', v),
        tip: 'Läuft noch in der kostenlosen Testphase; die erste Rechnung kommt danach. Noch kein Geld — und noch kein Hinweis auf einen Nichtzahler.',
        rang: 1,
      };
    case 'Bezahlt':
      return {
        label: 'Quartalsabo',
        ton: 'bezahlt',
        detail: mit('bezahlt', v),
        tip: 'Nach der Testphase bezahltes Quartalsabo (Zahlung über 1 €).',
        rang: 2,
      };
    case 'Upsell':
      return {
        label: 'Jahresabo',
        ton: 'bezahlt',
        detail: mit('bezahlt', v),
        tip: 'Jahresbezug mit Vorauszahlung, bezahlt.',
        rang: 3,
      };
    case 'Einmalfaktura':
      return {
        label: 'Einmalfaktura',
        ton: 'bezahlt',
        detail: mit('bezahlt', v),
        tip: 'Einmalkauf, bezahlt; kein laufendes Abo.',
        rang: 4,
      };
    case 'Unbezahlt': {
      const grund = unbezahltGrund(r);
      return {
        label: 'Unbezahlt',
        ton: 'offen',
        detail: grund === v ? grund : mit(grund, v),
        tip: 'Nach der Testphase nicht bezahlt (höchstens die Testphasen-Rechnung bis 1 €), widerrufen oder in der Testphase beendet.',
        rang: 5,
      };
    }
    default:
      return { label: r.subscription_type ?? '—', ton: 'neutral', detail: v, tip: '', rang: 8 };
  }
};

// ── Herkunft ───────────────────────────────────────────────────────────────────────────────────────────

export interface Herkunft {
  kanal: string;
  /** Blog wie die Übersicht „Je Blog" — nur im Sektions-Modus, sonst null. */
  blog: string | null;
  /** Lesbar: Webinar-Titel mit Datum, Direkt-Mailing mit Mail-Nr., sonst die Kennung. */
  detail: string;
  link: string | null;
  linkTitel: string | null;
  /** Rohe Kennung für Tooltip, Suche und Export. */
  kennung: string | null;
}

export const herkunft = (r: PlxOrderListRow, sektion = false): Herkunft => {
  const kanal = (sektion ? r.channel_label : null) || r.source || 'ohne Kennung';
  const blog = sektion ? r.blog_sektion : null;
  const kennung = r.effective_banner;
  if (r.webinar_id) {
    return {
      kanal,
      blog,
      detail: mit(r.webinar_title ?? r.webinar_id, datumKurz(r.webinar_date)),
      link: `/auswertungen/webinare/${encodeURIComponent(r.webinar_id)}`,
      linkTitel: 'Webinar-Auswertung öffnen',
      kennung,
    };
  }
  if (r.mailing_campaign) {
    return {
      kanal,
      blog,
      detail: mit(r.mailing_campaign, r.mailing_number ? `Mail ${r.mailing_number}` : ''),
      link: r.mailing_campaign_id
        ? `/plx-mailings?tab=evaluation&campaign=${r.mailing_campaign_id}`
        : null,
      linkTitel: 'Direkt-Mailing-Auswertung öffnen',
      kennung,
    };
  }
  return { kanal, blog, detail: kennung ?? '', link: null, linkTitel: null, kennung };
};

// ── Produkt, Preis, Geld ───────────────────────────────────────────────────────────────────────────────

export const preisText = (r: PlxOrderListRow): string => {
  if (!r.display_price) return '';
  if (r.is_one_time) return `${formatEur(r.display_price)} einmalig`;
  return `${formatEur(r.display_price)}/${r.is_upsell ? 'Jahr' : 'Quartal'}`;
};

export interface Geld {
  text: string;
  detail: string;
  warn: boolean;
  tip: string;
}

/** Rückbuchungen: PL-X liefert sie je nach Feld mit Vorzeichen; angezeigt wird der Betrag. */
const rueck = (v: number) => Math.abs(v) > 0.005;

/** Kein Betrag (null oder 0,00) — im Sektions-Modus zeigen Brutto und Netto dafür beide „—" (P5-Review). */
export const ohneBetrag = (v: number | null): boolean => v === null || Math.abs(v) < 0.005;

/** `sektion`: ein Umsatz von 0,00 € steht wie „keine Rechnung" als „—" (eine leere Form für Brutto und Netto). */
export const geld = (r: PlxOrderListRow, sektion = false): Geld => {
  const hinweise: string[] = [];
  if (rueck(r.ruecklastschrift_brutto))
    hinweise.push(`Rücklastschrift ${formatEur(Math.abs(r.ruecklastschrift_brutto))}`);
  if (rueck(r.storno_brutto)) hinweise.push(`Storno ${formatEur(Math.abs(r.storno_brutto))}`);
  if (rueck(r.rueckueberweisung_brutto))
    hinweise.push(`Rücküberweisung ${formatEur(Math.abs(r.rueckueberweisung_brutto))}`);
  if (rueck(r.ausbuchung_brutto))
    hinweise.push(`Ausbuchung ${formatEur(Math.abs(r.ausbuchung_brutto))}`);
  if (r.zahlung_brutto === null && r.actual_revenue === 0) {
    return {
      text: '—',
      detail: hinweise.join(' · '),
      warn: hinweise.length > 0,
      tip: 'Noch keine Rechnung von PL-X.',
    };
  }
  const nurTest = r.zahlung_brutto !== null && r.zahlung_brutto > 0 && r.zahlung_brutto <= 1;
  if (nurTest) hinweise.unshift('nur Testphasen-Rechnung');
  return {
    text: sektion && ohneBetrag(r.actual_revenue) ? '—' : formatEur(r.actual_revenue),
    detail: hinweise.join(' · '),
    warn: hinweise.length > 0,
    tip: TERMS.umsatz.tip,
  };
};

/** Netto je Anfo (P5); „—" bei Bestellungen, die keine Anfo sind, und ohne Zahlung. */
export const nettoText = (r: PlxOrderListRow): string =>
  ohneBetrag(r.paid_revenue_net) ? '—' : formatEur(r.paid_revenue_net);

// ── Kunde ──────────────────────────────────────────────────────────────────────────────────────────────

export interface Marke {
  label: string;
  tip: string;
  ton: 'gut' | 'warn' | 'schlecht' | 'neutral';
}

export const kundeName = (r: PlxOrderListRow): string =>
  [r.vorname, r.nachname].filter((x) => x).join(' ') || r.firma || '';

/**
 * Namen der Kundentypen: alte Liste kurz („Seriell", „Nie gezahlt"), Sektion wie ihre Übersichten
 * („Serieller Nichtzahler", „Früher bestellt, nie gezahlt", „Hat schon einmal gezahlt").
 */
export const kundentypName = (
  typ: 'erstbesteller' | 'frueher_gezahlt' | 'frueher_nie_gezahlt' | 'seriell',
  sektion = false,
): string => {
  switch (typ) {
    case 'erstbesteller':
      return sektion ? TERMS.erstbesteller.sektionLabel : TERMS.erstbesteller.short;
    case 'frueher_gezahlt':
      return sektion ? TERMS.frueherGezahlt.sektionLabel : TERMS.frueherGezahlt.short;
    case 'frueher_nie_gezahlt':
      return sektion ? TERMS.frueherNieGezahlt.sektionLabel : TERMS.frueherNieGezahlt.short;
    case 'seriell':
      return sektion ? TERMS.seriellerNichtzahler.label : TERMS.seriellerNichtzahler.short;
  }
};

/** Kundentyp und Auffälligkeiten — nur für Anfos mit bekanntem Zahlstand (wie in /auswertungen). */
export const kundeMarken = (r: PlxOrderListRow, sektion = false): Marke[] => {
  if (!r.is_anfo || r.is_provisional) return [];
  const out: Marke[] = [];
  if (r.customer_type === 'erstbesteller')
    out.push({ label: kundentypName('erstbesteller', sektion), tip: TERMS.erstbesteller.tip, ton: 'neutral' });
  if (r.customer_type === 'frueher_gezahlt')
    out.push({ label: kundentypName('frueher_gezahlt', sektion), tip: TERMS.frueherGezahlt.tip, ton: 'gut' });
  if (r.customer_type === 'frueher_nie_gezahlt' && !r.is_serial_nonpayer) {
    out.push({
      label: kundentypName('frueher_nie_gezahlt', sektion),
      tip: TERMS.frueherNieGezahlt.tip,
      ton: 'warn',
    });
  }
  if (r.is_serial_nonpayer)
    out.push({
      label: kundentypName('seriell', sektion),
      tip: TERMS.seriellerNichtzahler.tip,
      ton: 'schlecht',
    });
  if (r.is_duplicate)
    out.push({ label: TERMS.doppelbestellung.short, tip: TERMS.doppelbestellung.tip, ton: 'warn' });
  return out;
};

const hatRueckbuchung = (r: PlxOrderListRow) =>
  rueck(r.ruecklastschrift_brutto) ||
  rueck(r.storno_brutto) ||
  rueck(r.rueckueberweisung_brutto) ||
  rueck(r.ausbuchung_brutto);

// ── Schnellfilter ──────────────────────────────────────────────────────────────────────────────────────

export interface Schnellfilter {
  key: string;
  label: string;
  tip: string;
  test: (r: PlxOrderListRow) => boolean;
}

const bezahltTypen = new Set(['Bezahlt', 'Upsell', 'Einmalfaktura']);

export const SCHNELLFILTER: Schnellfilter[] = [
  {
    key: 'alle',
    label: 'Alle',
    tip: 'Alle Bestellungen der Liste, ohne Schnellfilter.',
    test: () => true,
  },
  {
    key: 'testphase',
    label: 'Testphase',
    tip: 'Läuft noch in der Testphase — noch keine Rechnung fällig.',
    test: (r) => !r.is_provisional && r.vertrag_sts !== 'V' && r.subscription_type === 'Testphase',
  },
  {
    key: 'bezahlt',
    label: 'Bezahlt',
    tip: 'Quartalsabo, Jahresabo oder Einmalkauf mit Zahlung über 1 €.',
    test: (r) =>
      !r.is_provisional && r.vertrag_sts !== 'V' && bezahltTypen.has(r.subscription_type ?? ''),
  },
  {
    key: 'unbezahlt',
    label: 'Unbezahlt',
    tip: 'Nach der Testphase nicht bezahlt, widerrufen oder in der Testphase beendet (ohne Verweigerte).',
    test: (r) => !r.is_provisional && r.vertrag_sts !== 'V' && r.subscription_type === 'Unbezahlt',
  },
  {
    key: 'gekuendigt',
    label: 'Gekündigt / widerrufen',
    tip: 'PL-X meldet eine Kündigung oder einen Widerruf.',
    test: (r) => Boolean(r.kuendigungsdatum) || r.vertrag_sts === 'W',
  },
  {
    key: 'auffaellig',
    label: 'Auffällig',
    tip: 'Serieller Nichtzahler, Doppelbestellung oder Rückbuchung (Rücklastschrift, Storno, Rücküberweisung, Ausbuchung).',
    test: (r) =>
      (r.is_anfo && !r.is_provisional && (r.is_serial_nonpayer || r.is_duplicate)) ||
      hatRueckbuchung(r),
  },
  {
    key: 'vorlaeufig',
    label: TERMS.vorlaeufig.label,
    tip: TERMS.vorlaeufig.tip,
    test: (r) => r.is_provisional,
  },
  {
    key: 'verweigert',
    label: 'Verweigert',
    tip: 'Von PL-X nicht angenommen — zählt nicht als Anfo.',
    test: (r) => !r.is_provisional && r.vertrag_sts === 'V',
  },
];

// ── Suche ──────────────────────────────────────────────────────────────────────────────────────────────

/**
 * Alles, was ein Mensch in das Suchfeld tippen könnte — kleingeschrieben, einmal je Zeile gebaut. Im Sektions-Modus
 * statt des alten Kanal-Labels der Kanal- und der Blog-Name der Übersichten: ein Name aus der Übersicht findet genau
 * deren Bestellungen.
 */
export const suchtext = (r: PlxOrderListRow, sektion = false): string =>
  [
    r.email,
    kundeName(r),
    r.firma,
    r.ordernumber,
    r.kunde,
    r.product_name,
    r.artikel,
    r.werbeaktion_wbkz,
    ...(sektion ? [herkunft(r, true).kanal, r.blog_sektion] : [r.source]),
    r.effective_banner,
    r.webinar_title,
    r.webinar_id,
    r.mailing_campaign,
  ]
    .filter((x) => x !== null && x !== undefined && x !== '')
    .join(' \u0001 ')
    .toLowerCase();

/** Welches Feld ein Übersichts-Name filtert, und ob er in beiden Übersichten vorkommt (Hinweis unter der Suche). */
export type NamensTreffer = {
  test: (r: PlxOrderListRow) => boolean;
  feld: 'kanal' | 'blog' | 'webinar';
  name: string;
  /** Der Name steht in der Kanal- UND der Blog-Übersicht (heute nur „ohne Kennung") — gefiltert wird der Kanal. */
  mehrdeutig: boolean;
};

const VORSATZ = /^(kanal|blog)\s*:\s*(.+)$/i;

/**
 * Sektions-Modus: ist die Eingabe GENAU ein Kanal- oder Blog-Name der Übersichten (Groß-/Kleinschreibung egal), gilt
 * der Name als Filter auf dieses Feld statt als Freitext — sonst fände „Webinar" auch Webinar-Titel in anderen Kanälen,
 * „ohne Kennung" auch „Kennung ohne Marke" (Wörter mit Und) und „Finanztrends" eine E-Mail-Domain. So liefert ein Name
 * aus der Übersicht genau deren Zahl (P5-Review 27.10.2026). `null` = kein Name, normale Suche.
 *
 * Genau EIN Feld je Name (Critical Review 28.10.): „ohne Kennung" heißt in beiden Übersichten gleich, eine Oder-Verknüpfung
 * zählte mehr als jede der beiden. Mit Vorsatz („Kanal: X", „Blog: X") gilt das genannte Feld; ohne Vorsatz der Kanal,
 * wenn es den Namen dort gibt, sonst der Blog.
 */
export const namensTreffer = (rows: PlxOrderListRow[], eingabe: string): NamensTreffer | null => {
  const roh = eingabe.trim();
  if (!roh) return null;
  const kanal = (r: PlxOrderListRow) => herkunft(r, true).kanal.toLowerCase();
  const blog = (r: PlxOrderListRow) => (r.blog_sektion ?? '').toLowerCase();
  const vorsatz = VORSATZ.exec(roh);
  if (vorsatz) {
    const feld = vorsatz[1].toLowerCase() === 'kanal' ? 'kanal' : 'blog';
    const name = vorsatz[2].trim();
    const q = name.toLowerCase();
    const wert = feld === 'kanal' ? kanal : blog;
    return { test: (r) => wert(r) === q, feld, name, mehrdeutig: false };
  }
  const q = roh.toLowerCase();
  const alsKanal = rows.some((r) => kanal(r) === q);
  const alsBlog = rows.some((r) => blog(r) === q);
  if (alsKanal) return { test: (r) => kanal(r) === q, feld: 'kanal', name: roh, mehrdeutig: alsBlog };
  if (alsBlog) return { test: (r) => blog(r) === q, feld: 'blog', name: roh, mehrdeutig: false };
  // Genau eine Webinar-Kennung (Link „+ N verweigert" der Detailseite): nur dieses Webinar — als Teilwort fände
  // „L9GkX8" auch die Wiederholung „L9GkX81027" (Bedienprüfung 28.10.).
  const webinar = (r: PlxOrderListRow) => (r.webinar_id ?? '').toLowerCase();
  return rows.some((r) => webinar(r) === q)
    ? { test: (r) => webinar(r) === q, feld: 'webinar', name: roh, mehrdeutig: false }
    : null;
};

export const namensFilter = (
  rows: PlxOrderListRow[],
  eingabe: string,
): ((r: PlxOrderListRow) => boolean) | null => namensTreffer(rows, eingabe)?.test ?? null;

/** Jedes Wort muss vorkommen (UND), Reihenfolge egal. */
export const passtSuche = (text: string, eingabe: string): boolean => {
  const woerter = eingabe
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w);
  return woerter.every((w) => text.includes(w));
};

// ── Sortierung ─────────────────────────────────────────────────────────────────────────────────────────

export type SortKey = 'bestellt' | 'produkt' | 'herkunft' | 'kunde' | 'stand' | 'umsatz' | 'netto';

export const sortWert = (r: PlxOrderListRow, key: SortKey, sektion = false): number | string | null => {
  switch (key) {
    case 'bestellt':
      return r.created_on;
    case 'produkt':
      return r.product_name;
    case 'herkunft': {
      const h = herkunft(r, sektion);
      return `${h.kanal} ${h.detail}`;
    }
    // Nach der E-Mail, die in der Zeile steht — ohne E-Mail („—", „Kundendaten folgen") ans Ende (Bedienprüfung 28.10.).
    case 'kunde':
      return r.email || null;
    case 'stand':
      return stand(r).rang;
    // Was als „—" dasteht, sortiert als leer (am Ende, in beiden Richtungen) — nicht als 0 vor dem kleinsten Betrag.
    case 'umsatz':
      return geld(r, sektion).text === '—' ? null : r.actual_revenue;
    case 'netto':
      return ohneBetrag(r.paid_revenue_net) ? null : r.paid_revenue_net;
  }
};

export const sortiere = (
  rows: PlxOrderListRow[],
  key: SortKey,
  dir: 'asc' | 'desc',
  sektion = false,
): PlxOrderListRow[] => {
  const f = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = sortWert(a, key, sektion);
    const y = sortWert(b, key, sektion);
    if (x === null && y === null) return 0;
    if (x === null || x === '') return 1;
    if (y === null || y === '') return -1;
    const c =
      typeof x === 'number' && typeof y === 'number'
        ? x - y
        : String(x).localeCompare(String(y), 'de');
    // Gleichstand: neueste zuerst, damit die Reihenfolge stabil und vorhersehbar bleibt.
    return c !== 0 ? c * f : b.created_on.localeCompare(a.created_on);
  });
};

// ── Summen der sichtbaren Auswahl (reine Zählungen und Beträge, `.claude/rules/react-frontend.md`) ─────────

export interface Summen {
  bestellungen: number;
  anfos: number;
  umsatz: number;
  /**
   * Bisher bezahlt netto (nur Anfos tragen einen Wert) OHNE vorläufig ergänzte — wie die Übersichten (P5-Review): deren
   * Testphasen-Beträge bis 1 € zählen in keinem Euro-Wert; die Zeile zeigt ihren Betrag trotzdem, als „vorläufig".
   */
  umsatzNetto: number;
}

export const summen = (rows: PlxOrderListRow[]): Summen =>
  rows.reduce(
    (s, r) => ({
      bestellungen: s.bestellungen + 1,
      anfos: s.anfos + (r.is_anfo ? 1 : 0),
      umsatz: s.umsatz + r.actual_revenue,
      umsatzNetto: s.umsatzNetto + (r.is_provisional ? 0 : (r.paid_revenue_net ?? 0)),
    }),
    { bestellungen: 0, anfos: 0, umsatz: 0, umsatzNetto: 0 },
  );

// ── Export „wie angezeigt“ ─────────────────────────────────────────────────────────────────────────────

const KUNDENTYP: Record<string, string> = {
  erstbesteller: TERMS.erstbesteller.label,
  frueher_gezahlt: TERMS.frueherGezahlt.label,
  frueher_nie_gezahlt: TERMS.frueherNieGezahlt.label,
};

/** `netto`: Spalte „Bisher bezahlt (netto)" dazu (Sektion /auswertungen/bestellungen); ohne den Schalter wie bisher. */
export const exportZeile = (r: PlxOrderListRow, netto = false): Record<string, string | number | null> => {
  const s = stand(r);
  const h = herkunft(r, netto);
  return {
    Bestelldatum: datumZeit(r.created_on),
    Bestellnummer: r.ordernumber,
    Kundennummer: r.kunde,
    'E-Mail': r.email,
    Name: kundeName(r) || null,
    Produkt: r.product_name,
    WKZ: r.werbeaktion_wbkz,
    'Preis (brutto)': r.display_price || null,
    Preisbasis: r.is_one_time ? 'einmalig' : r.is_upsell ? 'Jahr' : 'Quartal',
    Stand: s.label,
    'Stand Detail': s.detail || null,
    Vertragsstatus: r.vertrag_sts,
    Kündigung: datumKurz(r.kuendigungsdatum) || null,
    Aboende: datumKurz(r.aboende_datum) || null,
    'Testphase bis': datumKurz(r.trial_end) || null,
    Kanal: h.kanal,
    ...(netto ? { Blog: h.blog } : {}),
    Herkunft: h.detail || null,
    Kennung: r.effective_banner,
    Bestellseite: r.order_page,
    'Umsatz bezahlt (brutto)': r.actual_revenue,
    ...(netto ? { [TERMS.bezahltNettoListe.label]: r.paid_revenue_net } : {}),
    Rücklastschrift: rueck(r.ruecklastschrift_brutto) ? Math.abs(r.ruecklastschrift_brutto) : null,
    Storno: rueck(r.storno_brutto) ? Math.abs(r.storno_brutto) : null,
    Anfo: r.is_anfo ? 'ja' : 'nein',
    Kundentyp:
      r.is_anfo && !r.is_provisional && r.customer_type
        ? netto
          ? kundentypName(r.customer_type, true)
          : KUNDENTYP[r.customer_type]
        : null,
    'Serieller Nichtzahler': r.is_serial_nonpayer ? 'ja' : null,
    Doppelbestellung: r.is_duplicate ? 'ja' : null,
    'Vorläufig ergänzt': r.is_provisional ? 'ja' : null,
  };
};
